/**
 * Main converter: Legacy Format to React Flow
 * Orchestrates the conversion using modular converters
 */
import { KIND_TO_NODE_TYPE_MAP } from '../shared/constants/index'
import type {
  LegacyFormatData,
  LegacyFormatNode,
} from '../shared/legacyFormatTypes'
import type {
  SwitchboardEdge,
  SwitchboardFlowData,
  SwitchboardNode,
} from '../shared/reactFlowTypes'
import { NodeType } from '../shared/reactFlowTypes'
import {
  convertLegacyEndpointsToReactFlowEdges,
  createEdgesFromNodeData,
  recreateMissingOptionEndpoints,
} from './edgeConverters'
import { convertLegacyNodeToReactFlowNode } from './nodeConverters/index'

// Re-export for convenience
export type { LegacyFormatData, LegacyFormatNode }

const DYNAMIC_OPTION_NODE_KINDS = new Set([
  'auto_attendant',
  'call_distribution',
  'interactive_webhook',
])

type LegacySourceEndpoint = NonNullable<
  NonNullable<LegacyFormatNode['endpoints']>['sources']
>[number]
type OptionIdSourceEndpoint = LegacySourceEndpoint & { option_id: string }

function readChildOptionId(child: unknown): string | null {
  if (!child || typeof child !== 'object') {
    return null
  }

  const childId = (child as Record<string, unknown>).id
  if (typeof childId !== 'string') {
    return null
  }

  const trimmedChildId = childId.trim()
  return trimmedChildId === '' ? null : trimmedChildId
}

function isConnectedSourceEndpoint(source: LegacySourceEndpoint): boolean {
  return (
    typeof source.target_uuid === 'string' && source.target_uuid.trim() !== ''
  )
}

function normalizeChildrenArrayForDynamicOptions(
  children: unknown[],
  legacyNodeId: string,
): Record<string, unknown>[] {
  if (children.length === 0) {
    return [{}]
  }

  const first = children[0]
  const startsWithPlaceholder =
    typeof first === 'object' &&
    first !== null &&
    Object.keys(first as Record<string, unknown>).length === 0

  const normalizedChildren = startsWithPlaceholder
    ? [...children]
    : [{}, ...children]

  const seenIds = new Map<string, number>()

  return normalizedChildren.map((child, index) => {
    if (index === 0) {
      return {}
    }

    if (!child || typeof child !== 'object') {
      return {}
    }

    const childRecord = { ...(child as Record<string, unknown>) }
    const rawId =
      typeof childRecord.id === 'string' && childRecord.id.trim() !== ''
        ? childRecord.id.trim()
        : `${legacyNodeId}_option-missing-${index}`

    const occurrence = seenIds.get(rawId) || 0
    const canonicalId = occurrence === 0 ? rawId : `${rawId}__dup${occurrence}`
    seenIds.set(rawId, occurrence + 1)
    childRecord.id = canonicalId
    return childRecord
  })
}

function normalizeDynamicOptionIdentityNode(
  legacyNode: LegacyFormatNode,
): LegacyFormatNode {
  if (!DYNAMIC_OPTION_NODE_KINDS.has(legacyNode.kind)) {
    return legacyNode
  }

  if (!legacyNode.html || typeof legacyNode.html !== 'object') {
    return legacyNode
  }

  const childrenRaw = legacyNode.html.children
  if (!Array.isArray(childrenRaw)) {
    return legacyNode
  }

  const rawOptionChildren = childrenRaw.slice(1)
  const rawOptionIds = rawOptionChildren
    .map((child) => readChildOptionId(child))
    .filter((id): id is string => id !== null)
  const rawOptionIdSet = new Set(rawOptionIds)
  const hasInvalidOptionChild = rawOptionChildren.some(
    (child) => readChildOptionId(child) === null,
  )
  const hasDuplicateChildOptionIds = rawOptionIdSet.size !== rawOptionIds.length

  const existingSources = Array.isArray(legacyNode.endpoints?.sources)
    ? legacyNode.endpoints.sources
    : []
  const optionSources = existingSources.filter(
    (source): source is OptionIdSourceEndpoint =>
      !!source &&
      typeof source.option_id === 'string' &&
      source.option_id.trim() !== '',
  )
  const sourceOptionIds = optionSources.map((source) => source.option_id.trim())
  const sourceOptionIdSet = new Set(sourceOptionIds)
  const hasDuplicateSourceOptionIds =
    sourceOptionIdSet.size !== sourceOptionIds.length
  const hasUnknownSourceOptionIds = sourceOptionIds.some(
    (sourceOptionId) => !rawOptionIdSet.has(sourceOptionId),
  )
  const hasMissingSourceOptionIds = rawOptionIds.some(
    (childOptionId) => !sourceOptionIdSet.has(childOptionId),
  )

  const shouldNormalize =
    hasInvalidOptionChild ||
    hasDuplicateChildOptionIds ||
    hasDuplicateSourceOptionIds ||
    hasUnknownSourceOptionIds ||
    hasMissingSourceOptionIds

  if (!shouldNormalize) {
    return legacyNode
  }

  const normalizedChildren = normalizeChildrenArrayForDynamicOptions(
    childrenRaw,
    String(legacyNode.id).trim() || 'missing',
  )
  const optionChildren = normalizedChildren.slice(1)

  const canonicalOptionIds: string[] = []
  const canonicalToBaseId = new Map<string, string>()
  const firstCanonicalByBaseId = new Map<string, string>()

  optionChildren.forEach((childRecord) => {
    const canonicalId =
      typeof childRecord.id === 'string' && childRecord.id.trim() !== ''
        ? childRecord.id.trim()
        : ''
    if (!canonicalId) {
      return
    }

    const baseId = canonicalId.replace(/__dup\d+$/, '')
    canonicalOptionIds.push(canonicalId)
    canonicalToBaseId.set(canonicalId, baseId)
    if (!firstCanonicalByBaseId.has(baseId)) {
      firstCanonicalByBaseId.set(baseId, canonicalId)
    }
  })

  const nonOptionSources = existingSources.filter(
    (source) =>
      !source ||
      typeof source.option_id !== 'string' ||
      source.option_id.trim() === '',
  )

  const preferredByOptionId = new Map<string, LegacySourceEndpoint>()
  optionSources.forEach((source) => {
    const optionId = source.option_id.trim()
    const existing = preferredByOptionId.get(optionId)
    if (!existing) {
      preferredByOptionId.set(optionId, source)
      return
    }

    if (
      isConnectedSourceEndpoint(source) &&
      !isConnectedSourceEndpoint(existing)
    ) {
      preferredByOptionId.set(optionId, source)
    }
  })

  const normalizedOptionSources: LegacySourceEndpoint[] =
    canonicalOptionIds.map((canonicalId) => {
      const baseId = canonicalToBaseId.get(canonicalId) || canonicalId
      const canonicalSource = preferredByOptionId.get(canonicalId)
      const baseSource = preferredByOptionId.get(baseId)
      const templateSource =
        canonicalSource ||
        baseSource ||
        (firstCanonicalByBaseId.has(baseId)
          ? preferredByOptionId.get(firstCanonicalByBaseId.get(baseId) || '')
          : undefined)

      const targetUuid =
        templateSource && typeof templateSource.target_uuid === 'string'
          ? templateSource.target_uuid
          : null

      return {
        anchor: templateSource?.anchor || 'Right',
        is_source: true,
        uuid: `${canonicalId}_src`,
        target_uuid: targetUuid,
        my_label: templateSource?.my_label || 'Drag',
        option_id: canonicalId,
      }
    })

  return {
    ...legacyNode,
    html: {
      ...legacyNode.html,
      children: normalizedChildren,
    },
    endpoints: {
      sources: [...nonOptionSources, ...normalizedOptionSources],
      targets: Array.isArray(legacyNode.endpoints?.targets)
        ? legacyNode.endpoints?.targets
        : [],
    },
  }
}

function normalizeLegacyNodesForReactFlow(
  nodes: LegacyFormatData['nodes'],
): LegacyFormatData['nodes'] {
  const normalizedNodes: LegacyFormatData['nodes'] = {}

  Object.entries(nodes).forEach(([key, value]) => {
    if (!value) {
      return
    }

    if (Array.isArray(value)) {
      normalizedNodes[key] = value.map((node) =>
        normalizeDynamicOptionIdentityNode(node),
      )
      return
    }

    normalizedNodes[key] = normalizeDynamicOptionIdentityNode(value)
  })

  return normalizedNodes
}

/**
 * Map legacy node kind to React Flow node type
 */
function mapLegacyKindToNodeType(kind: string): NodeType | 'start' {
  return (
    KIND_TO_NODE_TYPE_MAP[kind as keyof typeof KIND_TO_NODE_TYPE_MAP] ||
    NodeType.Transfer
  )
}

/**
 * Convert legacy format to React Flow format
 *
 * @param legacyData - Legacy format data structure (may be wrapped in array)
 * @returns React Flow format data with nodes and edges
 *
 * @example
 * ```ts
 * const legacyData = {
 *   nodes: {
 *     start_node: { ... },
 *     direct_line: [{ ... }]
 *   }
 * }
 * const reactFlowData = convertLegacyFormatToReactFlow(legacyData)
 * ```
 */
export function convertLegacyFormatToReactFlow(
  legacyData: LegacyFormatData | LegacyFormatData[],
): SwitchboardFlowData {
  // Handle array format (legacy data is often wrapped in array)
  const data = Array.isArray(legacyData) ? legacyData[0] : legacyData

  if (!data || !data.nodes) {
    return { nodes: [], edges: [] }
  }

  const normalizedNodes = normalizeLegacyNodesForReactFlow(data.nodes)

  const nodes: SwitchboardNode[] = []
  const nodeMap = new Map<string, SwitchboardNode>()

  // Convert all nodes
  Object.entries(normalizedNodes).forEach(([key, value]) => {
    if (!value) return

    const nodeArray = Array.isArray(value) ? value : [value]

    nodeArray.forEach((legacyNode) => {
      const kind = legacyNode.kind || key
      const nodeType = mapLegacyKindToNodeType(kind)
      const reactFlowNode = convertLegacyNodeToReactFlowNode(
        legacyNode,
        nodeType,
        kind,
      )

      nodes.push(reactFlowNode)
      nodeMap.set(reactFlowNode.id, reactFlowNode)
    })
  })

  // Convert all edges from endpoints
  const allLegacyNodes: LegacyFormatNode[] = []
  // Create a map of node ID to legacyNode for later reference
  Object.values(normalizedNodes).forEach((value) => {
    if (value) {
      const nodeArray = Array.isArray(value) ? value : [value]
      nodeArray.forEach((legacyNode) => {
        allLegacyNodes.push(legacyNode)
      })
    }
  })

  const edges = convertLegacyEndpointsToReactFlowEdges(allLegacyNodes, nodeMap)

  // CRITICAL: Recreate option endpoints from children[] if Legacy Admin filtered them out
  // This addresses the issue where Legacy Admin's save condition fails:
  // connectionSource.sourceId != connectionSource.endpoints[0].anchor.elementId
  recreateMissingOptionEndpoints(allLegacyNodes, nodeMap)

  // Extract connection fields from edges and store in node data
  // This ensures nodes have connection target IDs (e.g., schedule.whenOpen, queue.onTimeout)
  extractConnectionFieldsFromEdges(nodes, edges)

  // Create edges from custom handle fields in nodes
  // This handles nodes that store connection targets in their data fields
  // (e.g., schedule.whenOpen, queue.onTimeout, etc.)
  createEdgesFromNodeData(nodes, edges, nodeMap)

  return { nodes, edges }
}

/**
 * Extract connection fields from edges and store them in node data
 * This is needed for round-trip conversion - nodes need to have connection target IDs
 * stored in their data so they can be converted back to legacy format.
 * Only sets fields if they're not already set from HTML mapping.
 */
function extractConnectionFieldsFromEdges(
  nodes: SwitchboardNode[],
  edges: SwitchboardEdge[],
): void {
  // Create a map of source node ID + sourceHandle to target node ID
  const connectionMap = new Map<string, string>()
  edges.forEach((edge) => {
    if (edge.sourceHandle && edge.target) {
      const key = `${edge.source}-${edge.sourceHandle}`
      connectionMap.set(key, edge.target)
    }
  })

  // Store connection fields in node data (only if not already set from HTML)
  nodes.forEach((node) => {
    if (!node.data) return

    if (node.type === NodeType.Schedule) {
      const whenOpenKey = `${node.id}-whenOpen`
      const whenClosedKey = `${node.id}-whenClosed`
      const holidayKey = `${node.id}-holiday`

      const whenOpenTarget = connectionMap.get(whenOpenKey)
      const whenClosedTarget = connectionMap.get(whenClosedKey)
      const holidayTarget = connectionMap.get(holidayKey)

      // Only set if not already set from HTML mapping
      if (whenOpenTarget && !node.data.whenOpen) {
        node.data.whenOpen = whenOpenTarget
      }
      if (whenClosedTarget && !node.data.whenClosed) {
        node.data.whenClosed = whenClosedTarget
      }
      if (holidayTarget && !node.data.holiday) {
        node.data.holiday = holidayTarget
      }
    }

    if (node.type === NodeType.Queue) {
      const onTimeoutKey = `${node.id}-onTimeout`
      const onEscapeKey = `${node.id}-onEscape`

      const onTimeoutTarget = connectionMap.get(onTimeoutKey)
      const onEscapeTarget = connectionMap.get(onEscapeKey)

      // Only set if not already set from HTML mapping
      if (onTimeoutTarget && !node.data.onTimeout) {
        node.data.onTimeout = onTimeoutTarget
      }
      if (onEscapeTarget && !node.data.onEscape) {
        node.data.onEscape = onEscapeTarget
      }
    }
  })
}
